import Dialog from "@/components/ui/dialog";
import { sendFeedback } from "@/lib/sentry/sendFeedback";
import { useAuth0 } from "@auth0/auth0-react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircleQuestion } from "lucide-react";
import { ReactNode, useRef, useState } from "react";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Separator } from "./separator";
import { Textarea } from "./textarea";

interface Props {
  children?: (toggleFeedbackModal: () => void) => ReactNode;
}

const Feedback = ({ children }: Props) => {
  const { user } = useAuth0();
  const [open, setOpen] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  function handleSubmit() {
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const message = (formData.get("message") as string) || undefined;
    const name = (formData.get("name") as string) || user?.name;
    const email = (formData.get("email") as string) || user?.email;

    if (!message || !name || !email) return;

    sendFeedback({ message, email, name }, { includeReplay: true })
      .then(() => {
        setFeedbackSent(true);
        setTimeout(() => setOpen(false), 2500);
        setTimeout(() => setFeedbackSent(false), 3000);
      })
      .catch((error) => {
        console.error(error);
        setFeedbackError(true);
        setTimeout(() => setOpen(false), 3000);
        setTimeout(() => setFeedbackError(false), 3500);
      });
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setFeedbackSent(false);
      }}
    >
      {children ? (
        children(() => setOpen((prev) => !prev))
      ) : (
        <Dialog.Trigger asChild>
          <div className="transition-colors hover:text-brand-gray-900 dark:hover:text-brand-gray-50">
            <MessageCircleQuestion
              strokeWidth={1.6}
              className="h-[20px] w-[20px]"
            />
          </div>
        </Dialog.Trigger>
      )}
      <Dialog.Content>
        <AnimatePresence mode="wait">
          {!feedbackSent && !feedbackError && (
            <motion.div
              key="formContent"
              className="relative overflow-hidden px-0.5"
              initial={{ top: "8px", opacity: 0 }}
              animate={{ top: "0px", opacity: 1 }}
              exit={{ top: "8px", opacity: 0, height: "24px" }}
            >
              <Dialog.Header>
                <Dialog.Title>Help make us better</Dialog.Title>
                <Dialog.Description>
                  Provide feedback, report bugs and ideas to help us make the
                  experience better.
                </Dialog.Description>
              </Dialog.Header>
              <Separator className="my-2" />
              <form
                ref={formRef}
                onChange={(e) =>
                  setDisableSubmit(!e.currentTarget.checkValidity())
                }
              >
                {!user && (
                  <>
                    <Label className="text-gray-600">Name *</Label>
                    <Input className="mb-3" name="name" required />
                    <Label className="text-gray-600">Email *</Label>
                    <Input
                      className="mb-3"
                      name="email"
                      type="email"
                      required
                    />
                  </>
                )}
                <Label className="text-gray-600">Feedback *</Label>
                <Textarea name="message" required className="min-h-32" />
              </form>
              <Dialog.Footer className="mt-4 gap-2">
                <Dialog.Close asChild>
                  <Button size="sm" variant="outline">
                    Close
                  </Button>
                </Dialog.Close>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  variant="default"
                  disabled={disableSubmit}
                >
                  Submit
                </Button>
              </Dialog.Footer>
            </motion.div>
          )}
          {feedbackSent && (
            <motion.div
              key="submitCompleteContent"
              className="relative text-center"
              initial={{ top: "8px", opacity: 0 }}
              animate={{ top: "0px", opacity: 1 }}
              exit={{ top: "8px", opacity: 0 }}
            >
              Thank you for your feedback
            </motion.div>
          )}
          {feedbackError && (
            <motion.div
              key="submitErrorContent"
              className="relative text-center"
              initial={{ top: "8px", opacity: 0 }}
              animate={{ top: "0px", opacity: 1 }}
              exit={{ top: "8px", opacity: 0 }}
            >
              Unable to submit your feedback. Please try again later.
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default Feedback;
